import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Box, Button, Typography, IconButton } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { Images } from "../../assets/common";
import AddPopup from "../AddPopup";
import SubscriptionPopup from "../SubscriptionPopup";
import LeftBarUpload from "./LeftBarUpload";
import LockIcon from '@mui/icons-material/Lock';
import PremiumBadgeIcon from "../../assets/common/icons/premium-badge.png";
import "./index.scss";
import GoLiveButton from './GoLive_List';
import { getUserDetail, hasPremium as checkHasPremium, hasProTier } from "../../constants/function";
import notifier from "../Notifier";
import { useAuth } from "../../views/parent/SkAuth";

export default function LeftBar({ isNavOpen, currentScreenWidth, onOpenChat = () => {}, onOpenNotifications = () => {}, onOpenWallet = () => {} }) {
  const history = useHistory();
  const { t } = useTranslation("common");
  const theme = useTheme();
  const auth = useAuth();
  const isAdmin = auth?.isAdmin || false;
  const [activeIndex, setActiveIndex] = useState(0);
  const [showPremiumPopup, setShowPremiumPopup] = useState(false);
  const [userSubscription, setUserSubscription] = useState(null);
  const isMobileNav = currentScreenWidth <= 600;

  const menuItems = [
    {
      index: 0,
      path: "/app/Home",
      label: t("sidebar.home", { defaultValue: "Home" }),
      image: Images.home_default,
      activeImage: Images.home_enter,
    },
    {
      index: 1,
      path: "/app/Library",
      label: t("sidebar.library", { defaultValue: "Library" }),
      image: Images.library_default,
      activeImage: Images.library_enter,
    },
    {
      index: 2,
      path: "/app/Feed",
      label: t("sidebar.feed", { defaultValue: "Feed" }),
      image: Images.feed_default,
      activeImage: Images.feed_enter,
    },
    {
      index: 3,
      path: "/ai-chat.html",
      label: "SIKI AI",
      image: Images.feed_default,
      activeImage: Images.feed_enter,
      requiresSubscription: true,
      isExternalLink: true,
    },
    {
      index: 4,
      path: "/app/Subscription",
      label: t("sidebar.subscription", { defaultValue: "Subscription" }),
      image: Images.subs_default,
      activeImage: Images.subs_enter
    },
    {
      index: 5,
      path: null,
      label: "Premium",
      isPremium: true,
    },
    {
      index: 6,
      path: "/app/PowerPlays",
      label: t("sidebar.powerplays", { defaultValue: "PowerPlays" }),
      image: Images.powerplays_default,
      activeImage: Images.powerplays_active,
    },
    // Gamification v2
    {
      index: 7,
      path: "/app/Missions",
      label: t("sidebar.missions", { defaultValue: "Missions" }),
      image: Images.missions_default,
      activeImage: Images.missions_default,
      group: "nav",
    },
    {
      index: 8,
      path: "/app/Analytics",
      label: t("sidebar.analytics", { defaultValue: "Analytics" }),
      image: Images.insights_default,
      activeImage: Images.insights_default,
      group: "nav",
    },
  ];

  useEffect(() => {
    const determineActiveIndex = (path) => {
      const item = menuItems.find((menuItem) => menuItem.path && path.startsWith(menuItem.path));
      return item ? item.index : 0;
    };
    setActiveIndex(determineActiveIndex(history.location.pathname));
  }, [history.location.pathname]);

  useEffect(() => {
    setUserSubscription(checkHasPremium());
  }, []);

  const handleMenuClick = async (item) => {
    if (item.isPremium) {
      setShowPremiumPopup(true);
    } else if (item.requiresSubscription && !userSubscription && !isAdmin) {
      notifier.warning("Siki AI requires a premium subscription");
      setShowPremiumPopup(true);
    } else if (item.path) {
      setActiveIndex(item.index);
      if (item.isExternalLink) {
        window.location.href = item.path;
      } else {
        history.push(item.path);
      }
    }
  };

  // Split menu items into navigation and pro sections
  const visible = menuItems.filter(item => !item.hidden);
  const navItems = visible.filter(item => item.group === "nav" || (!item.group && !item.isPremium && item.index <= 4));
  const proItems = visible.filter(item => item.group === "pro" || (!item.group && (item.isPremium || item.index >= 6 || item.requiresPro)));

  return (
  <Box sx={{ display: "flex" }}>
  <Box
  id="sideBarOuter"
  className="__sidebar_content"
  sx={{
    position: "fixed",
    left: 0,
    bottom: 0,
    width: "220px",
    zIndex: 10,
    boxShadow: "none",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundColor: "var(--hud-sidebar-bg, #08080C)",
    borderRight: "1px solid var(--hud-border, #16161F)",
    transform: isNavOpen ? "translateX(0) translateZ(0)" : "translateX(-100%) translateZ(0)",
    pointerEvents: isNavOpen ? "auto" : "none",
    transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
    willChange: "transform",
    overflowY: "auto",
  }}
>
      <Box
        className="__menu_item"
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "4px",
          pb: 2,
        }}
      >

{/* Go Live Button */}
<GoLiveButton
  buttonSx={{
    width: "100%",
    height: "40px",
    borderRadius: "4px",
    border: "1px solid var(--hud-accent, #FFEE20)",
    background: "transparent",
    color: "var(--hud-accent, #FFEE20)",
    fontFamily: "var(--font-body)",
    fontSize: "13px",
    fontWeight: 700,
    letterSpacing: "1px",
    textTransform: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    position: "relative",
    overflow: "hidden",
    transition: "all 0.25s ease",
    marginBottom: "6px",
    padding: "0",
    "&:hover": {
      background: "var(--hud-accent, #FFEE20)",
      color: "#000",
      boxShadow: "0 0 24px rgba(255, 238, 32,0.15)",
    },
  }}
  iconSx={{
    width: 0,
    height: 0,
    marginRight: 0,
    display: "none",
  }}
/>

{/* Upload Button */}
<LeftBarUpload
  buttonSx={{
    width: "100%",
    height: "36px",
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    textTransform: "none",
    padding: "0",
    borderRadius: "4px",
    background: "transparent",
    border: "1px solid var(--glass-border, rgba(255,255,255,0.06))",
    color: "var(--hud-text-muted, #666)",
    fontFamily: "var(--font-display)",
    fontWeight: 600,
    fontSize: "13px",
    letterSpacing: "0",
    transition: "all 0.2s ease",
    marginBottom: "6px",
    "&:hover": {
      borderColor: "rgba(255, 238, 32,0.25)",
      color: "var(--hud-accent, #FFEE20)",
      background: "transparent",
    },
  }}
  iconSx={{
    width: 14,
    height: 14,
    marginRight: "7px",
    color: "inherit",
  }}
/>

{/* Divider + Navigate label */}
<Box className="hud-sidebar-divider" />
<Typography className="hud-sidebar-label">Navigate</Typography>

        {isNavOpen && isMobileNav ? (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
              gap: 1,
            }}
          >
            <IconButton onClick={onOpenChat} sx={{ width: 34, height: 34, borderRadius: "4px", border: "1px solid var(--hud-border)", background: "var(--hud-surface)", "&:hover": { borderColor: "rgba(255, 238, 32,0.2)" } }}>
              <img src={Images.chat} alt="Chat" style={{ width: "15px", height: "15px", filter: "var(--icon-filter, brightness(0) invert(1))", objectFit: "contain" }} />
            </IconButton>
            <IconButton onClick={onOpenNotifications} sx={{ width: 34, height: 34, borderRadius: "4px", border: "1px solid var(--hud-border)", background: "var(--hud-surface)", "&:hover": { borderColor: "rgba(255, 238, 32,0.2)" } }}>
              <img src={Images.notification} alt="Notifications" style={{ width: "15px", height: "15px", filter: "var(--icon-filter, brightness(0) invert(1))", objectFit: "contain" }} />
            </IconButton>
            <IconButton onClick={onOpenWallet} sx={{ width: 34, height: 34, borderRadius: "4px", border: "1px solid var(--hud-border)", background: "var(--hud-surface)", "&:hover": { borderColor: "rgba(255, 238, 32,0.2)" } }}>
              <img src={Images.wallet_icon} alt="Wallet" style={{ width: "15px", height: "15px", filter: "var(--icon-filter, brightness(0) invert(1))", objectFit: "contain" }} />
            </IconButton>
          </Box>
        ) : null}

        {/* Navigation Items */}
        {isNavOpen &&
          navItems.map((item) => (
            <Button
              key={item.index}
              className={`__item navButton ${activeIndex === item.index ? "navButton--active" : ""} ${item.requiresSubscription && !userSubscription && !isAdmin ? "hud-premium-item" : ""}`}
              onClick={() => handleMenuClick(item)}
              sx={{
                width: "100%",
                justifyContent: "flex-start",
                alignItems: "center",
                display: "flex",
                textTransform: "none",
                padding: "9px 12px",
                borderRadius: "4px",
                fontFamily: "var(--font-display)",
                fontSize: "13px",
                fontWeight: 500,
                letterSpacing: "0.5px",
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 16,
                  height: 16,
                  marginRight: "12px",
                  display: "inline-block",
                  verticalAlign: "middle",
                  backgroundColor: activeIndex === item.index
                    ? "var(--hud-accent, #FFEE20)"
                    : "var(--hud-text-dim, #3A3A50)",
                  mask: `url("${item.image}") center / contain no-repeat`,
                  WebkitMask: `url("${item.image}") center / contain no-repeat`,
                  transition: "background-color 0.2s ease",
                }}
              />
              <Typography
                variant="body2"
                component="span"
                className="nav-label"
                sx={{
                  color: item.requiresSubscription && !userSubscription && !isAdmin
                    ? "var(--hud-accent, #FFEE20)"
                    : activeIndex === item.index
                    ? "var(--hud-accent, #FFEE20)"
                    : "var(--hud-text-muted, #6A6A80)",
                  fontFamily: "var(--font-display)",
                  fontWeight: activeIndex === item.index ? 600 : 500,
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  transition: "all 0.2s ease",
                }}
              >
                {item.label}
                {item.requiresSubscription && !userSubscription && !isAdmin && (
                  <LockIcon sx={{ fontSize: 12, color: "var(--hud-accent, #FFEE20)" }} />
                )}
              </Typography>
            </Button>
          ))}

        {/* Pro Section */}
        <Box className="hud-sidebar-divider" />
        <Typography className="hud-sidebar-label">Pro</Typography>

        {isNavOpen &&
          proItems.map((item) => (
            <Button
              key={item.index}
              className={`__item navButton ${activeIndex === item.index ? "navButton--active" : ""} ${item.isPremium ? "hud-premium-item" : ""}`}
              onClick={() => handleMenuClick(item)}
              sx={{
                width: "100%",
                justifyContent: "flex-start",
                alignItems: "center",
                display: "flex",
                textTransform: "none",
                padding: "9px 12px",
                borderRadius: "4px",
                fontFamily: "var(--font-display)",
                fontSize: "13px",
                fontWeight: 500,
                letterSpacing: "0.5px",
              }}
            >
              {item.isPremium ? (
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    marginRight: '12px',
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    background: `url(${PremiumBadgeIcon}) no-repeat center`,
                    backgroundSize: 'contain',
                    filter: 'brightness(0) saturate(100%) invert(82%) sepia(52%) saturate(700%) hue-rotate(7deg) brightness(100%) contrast(90%)',
                  }}
                />
              ) : (
                <Box
                  component="span"
                  sx={{
                    width: 16,
                    height: 16,
                    marginRight: "12px",
                    display: "inline-block",
                    verticalAlign: "middle",
                    backgroundColor: "var(--hud-accent, #FFEE20)",
                    mask: `url("${item.image}") center / contain no-repeat`,
                    WebkitMask: `url("${item.image}") center / contain no-repeat`,
                  }}
                />
              )}
              <Typography
                variant="body2"
                component="span"
                className={item.index === 6 ? "hud-powerplays-label" : ""}
                sx={{
                  color: "var(--hud-accent, #FFEE20)",
                  fontFamily: "var(--font-display)",
                  fontWeight: 600,
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                }}
              >
                {item.label}
                {item.requiresPro && !hasProTier() && !isAdmin && (
                  <LockIcon sx={{ fontSize: 12, color: "var(--hud-accent, #FFEE20)" }} />
                )}
              </Typography>
              {item.isPremium && (
                <span className="hud-pro-badge">PRO</span>
              )}
            </Button>
          ))}
      </Box>

      {/* System Info Footer */}
      <Box className="hud-sidebar-sys">
        <span className="hud-sys-accent">SYS</span> // NETWORK ACTIVE<br/>
        <span className="hud-sys-accent">USR</span> // AUTHENTICATED<br/>
        <span className="hud-sys-accent">VER</span> // 2.0.4-BETA
      </Box>
    </Box>

    <AddPopup
      api="playlist"
      title={t("addPopup.title1")}
      open={false}
      handleClose={() => {}}
    />
    <SubscriptionPopup
      isOpen={showPremiumPopup}
      onClose={() => {
        setShowPremiumPopup(false);
        setUserSubscription(checkHasPremium());
      }}
    />
  </Box>
);
}
